import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Row, Col, Form } from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../state_manager/hooks/useAppDispatch'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { TasksSection } from '../../../tasks/components/TasksSection.tsx'
import { InteractionsSection } from '../../../interactions/components/InteractionsSection.tsx'
import { DetailPanel, type DetailPanelItemType, type DetailPanelMode } from '../../../deals/components/forms/parts/DetailPanel.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { buildAccountRoleOptions } from '../../../../core/utils/accountRoleOptions.ts'
import { createTask, updateTask, deleteTask as deleteTaskThunk } from '../../../../store/slices/tasksSlice'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForAccount,
  delinkInteraction
} from '../../../interactions/services/interactionsService.ts'
import { updateCompanyRecord } from '../../../../state_manager/state_update_actions/companies/index.ts'
import {
  setActiveRecord,
  updateActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  addInteraction,
  updateInteraction as updateInteractionAction,
  setInteractions,
  removeInteraction
} from '../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { TaskRecord, TaskRecordFields } from '../../../../state_manager/data_models/task_record.ts'
import { InteractionRecordFields } from '../../../../state_manager/data_models/interaction_record.ts'
import { getCompanyInfoFields } from '../../utils/fieldConfigs.ts'
import { handleFieldChange as handleBusinessRules } from '../../utils/companyFormBusinessRules.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { AccountRecord } from '../../../../state_manager/data_models/account_record.ts'
import { SECTION_COMPANY_OVERVIEW } from '../../../../state_manager/constants/strings.ts'
import { RoundOverviewSection } from './tabs/RoundOverviewSection.tsx'
import { GeneralSection } from './tabs/GeneralSection.tsx'
import { InvestmentSummarySection } from './tabs/InvestmentSummarySection.tsx'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'

const MemoizedTasksSection = React.memo(function MemoizedTasksSection(props: any) {
  return <TasksSection {...props} />
})

const MemoizedInteractionsSection = React.memo(function MemoizedInteractionsSection(props: any) {
  return <InteractionsSection {...props} />
})

export function Drawer({
  company = null,
  onSubmit,
  loading = false,
  onRefreshInteractionsReady,
  onRefreshTasksReady,
  onRefreshInvestmentMeasuresReady
}: {
  company?: AccountRecord | null
  onSubmit?: (data: any) => Promise<void>
  loading?: boolean
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
  onRefreshInvestmentMeasuresReady?: (refreshFn: () => Promise<void>) => void
}) {
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()
  const { getLookupOptions } = useLookups()

  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const investorClassifications = useSelector((state: any) => state.globalConfigs?.investor_classifications || [])

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getInvestorClassificationOptions = useCallback(() => {
    return investorClassifications.map((classification: any) => ({
      value: classification.id,
      label: classification.label
    }))
  }, [investorClassifications])

  const accountId = company?.[AccountRecordFields.accounts__id]
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)
  const refreshInvestmentMeasuresRef = useRef<(() => Promise<void>) | null>(null)

  const companyFromActiveRecords = useSelector((state: any) =>
    accountId ? selectActiveRecordById(state, accountId) : null
  )
  const currentCompany = companyFromActiveRecords || company

  const previousAccountIdRef = useRef<string | null | undefined>(null)

  const tasks = useMemo(() => {
    const tasksRaw = currentCompany?.[AccountRecordFields.prog__tasks]
    return Array.isArray(tasksRaw) ? tasksRaw : []
  }, [currentCompany])

  useEffect(() => {
    const accountIdString = accountId?.toString()
    const previousAccountIdString = previousAccountIdRef.current?.toString()

    if (accountIdString && accountIdString !== previousAccountIdString && company) {
      dispatch(setActiveRecord({ uuid: accountIdString, record: company }))
      dispatch(registerRecordUsage({ uuid: accountIdString, context: 'drawer' }))
      previousAccountIdRef.current = accountId
    }

    return () => {
      if (previousAccountIdString) {
        dispatch(unregisterRecordUsage({ uuid: previousAccountIdString, context: 'drawer' }))
      }
    }
  }, [accountId, dispatch])

  const searchParams = new URLSearchParams(location.search)
  const hideInteractions = searchParams.get('hideInteractions') === 'true'
  const initialTaskId = searchParams.get('taskId')

  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectedItemType, setSelectedItemType] = useState<DetailPanelItemType | null>(null)
  const [detailMode, setDetailMode] = useState<DetailPanelMode>('view')
  const [isTaskInEditMode, setIsTaskInEditMode] = useState(false)

  const [form] = Form.useForm()

  const [formData, setFormData] = useState<Record<string, any>>({})

  useEffect(() => {
    const isDetailPanelOpen =
      (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction'

    const isTaskOrInteractionEditing =
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (detailMode === 'add-task' ||
        detailMode === 'edit-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-interaction' ||
        selectedItem)

    if (isDetailPanelOpen) {
      setRightMenuWidth('55%')
    } else if (isTaskOrInteractionEditing) {
      setRightMenuWidth('52.5%')
    } else {
      resetRightMenuWidth()
    }

    return () => {
      resetRightMenuWidth()
    }
  }, [selectedItem, detailMode, selectedItemType, setRightMenuWidth, resetRightMenuWidth])

  useEffect(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
    setIsTaskInEditMode(false)
  }, [accountId])

  useEffect(() => {
    if (initialTaskId && tasks && tasks.length > 0) {
      const targetTask = tasks.find((task: any) => {
        const taskId = task[TaskRecordFields.tasks__id]
        return taskId && taskId.toString() === initialTaskId.toString()
      })
      if (targetTask) {
        setSelectedItem(targetTask)
        setSelectedItemType('task')
        setDetailMode('view')
      }
    }
  }, [initialTaskId, tasks])

  useEffect(() => {
    if (currentCompany) {
      const rolesArray = currentCompany.prog__account_roles || []
      const roleIds = Array.isArray(rolesArray)
        ? rolesArray
            .map((role: any) => {
              if (typeof role === 'string') return role
              if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
                return role.account_roles__account_role_choice_id
              }
              return null
            })
            .filter((id: any): id is string => id !== null)
        : []

      const initialData: Record<string, any> = {
        [AccountRecordFields.accounts__name]: currentCompany[AccountRecordFields.accounts__name] || '',
        [AccountRecordFields.accounts__industry]: currentCompany[AccountRecordFields.accounts__industry] || '',
        [AccountRecordFields.accounts__website]: currentCompany[AccountRecordFields.accounts__website] || '',
        [AccountRecordFields.accounts__description]: currentCompany[AccountRecordFields.accounts__description] || '',
        [AccountRecordFields.accounts__investor_classification_id]:
          currentCompany[AccountRecordFields.accounts__investor_classification_id] ?? null,
        [AccountRecordFields.accounts__business_city_label]:
          currentCompany[AccountRecordFields.accounts__business_city_label] || '',
        [AccountRecordFields.accounts__business_country_label]:
          currentCompany[AccountRecordFields.accounts__business_country_label] || '',
        [AccountRecordFields.accounts__aliases]: currentCompany[AccountRecordFields.accounts__aliases] || [],
        prog__account_roles: roleIds
      }
      setFormData(initialData)
    }
  }, [currentCompany])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => {
        const businessRuleResult = handleBusinessRules(field, value, prev, form)
        return businessRuleResult.formData
      })
    },
    [form]
  )

  const handlePatchSuccess = useCallback(
    (recordId: string, updates: Record<string, any>) => {
      dispatch(
        updateCompanyRecord({
          companyId: recordId,
          updates
        })
      )
      dispatch(
        updateActiveRecord({
          uuid: recordId,
          updates
        })
      )
      setFormData((prev) => {
        const updated = { ...prev }
        Object.keys(updates).forEach((key) => {
          if (key in updates) {
            updated[key] = updates[key]
          }
        })
        return updated
      })
    },
    [dispatch]
  )

  const handleTaskSelect = useCallback((task: any) => {
    if (task === null) {
      setSelectedItem(null)
      setSelectedItemType(null)
      setDetailMode('view')
    } else {
      setSelectedItem(task)
      setSelectedItemType('task')
      setDetailMode('view')
    }
  }, [])

  const handleAddTask = useCallback(() => {
    setDetailMode('add-task')
    setSelectedItem(null)
    setSelectedItemType('task')
  }, [])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
    setDetailMode('view')
  }, [])

  const handleAddInteraction = useCallback(() => {
    setDetailMode('add-interaction')
    setSelectedItem(null)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
  }, [])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!accountId || !id || typeof accountId !== 'string') {
        throw new Error('Account ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId: accountId, taskId: id, recordType: 'company' })).unwrap()
          setTimeout(() => {
            handleDetailClose()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailClose()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(accountId as string, interactionId, currentToken)
        dispatch(removeInteraction(accountId as string, interactionId))
        const refreshedInteractions = await fetchInteractionsForAccount(accountId as string, {}, currentToken)
        dispatch(setInteractions(accountId as string, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [accountId, dispatch, handleDetailClose, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!accountId || !interactionId || typeof accountId !== 'string') {
        throw new Error('Account ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(accountId as string, interactionId, 'company', currentToken)
        dispatch(removeInteraction(accountId as string, interactionId))
        const refreshedInteractions = await fetchInteractionsForAccount(accountId as string, {}, currentToken)
        dispatch(setInteractions(accountId as string, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [accountId, dispatch, handleDetailClose]
  )

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const handleDetailSubmit = async (data: any, mode: string) => {
    if (!accountId || accountId === 'new' || typeof accountId !== 'string') {
      return
    }

    try {
      if (mode === 'add-task') {
        const result = await dispatch(
          createTask({ dealId: accountId as string, taskData: data, recordType: 'company' }) as any
        )

        if (result.payload?.task && currentCompany) {
          const existingTasks = currentCompany[AccountRecordFields.prog__tasks] || []
          const updatedTasks = [result.payload.task, ...existingTasks]
          dispatch(
            updateActiveRecord({
              uuid: accountId as string,
              updates: {
                [AccountRecordFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'edit-task') {
        const updateTaskAction = updateTask({
          dealId: accountId as string,
          taskId: selectedItem?.[TaskRecordFields.tasks__id],
          taskData: data,
          recordType: 'company'
        })
        const result = await dispatch(updateTaskAction as any)

        if (result.payload?.task && currentCompany) {
          const existingTasks: TaskRecord[] = currentCompany[AccountRecordFields.prog__tasks] || []
          const taskId = result.payload.task[TaskRecordFields.tasks__id] || selectedItem?.[TaskRecordFields.tasks__id]
          const updatedTasks = existingTasks.map((t) => {
            const existingTaskId = t?.[TaskRecordFields.tasks__id]
            if (existingTaskId?.toString() === taskId?.toString()) {
              return result.payload.task
            }
            return t
          })
          dispatch(
            updateActiveRecord({
              uuid: accountId as string,
              updates: {
                [AccountRecordFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'add-interaction') {
        const interactionData = {
          ...data
        }
        const currentToken = getAccessToken(store.getState().auth)
        const createdInteraction = await createInteraction(
          accountId as string,
          interactionData,
          currentToken,
          'company'
        )

        dispatch(addInteraction(accountId as string, createdInteraction as any))
      } else if (mode === 'edit-interaction') {
        const interactionData = {
          ...data
        }
        const interactionId = selectedItem?.[InteractionRecordFields.interactions__id]
        const currentToken = getAccessToken(store.getState().auth)
        const updatedInteraction = await updateInteraction(
          accountId as string,
          interactionId,
          interactionData,
          currentToken,
          'company'
        )

        dispatch(updateInteractionAction(accountId as string, interactionId, updatedInteraction as any))
      }

      setDetailMode('view')
    } catch (error) {
      console.error('Failed to save:', error)
      throw error
    }
  }

  const isFullWidthMode = useMemo(() => {
    return (
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (selectedItem ||
        detailMode === 'add-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-task' ||
        detailMode === 'edit-interaction')
    )
  }, [selectedItemType, selectedItem, detailMode])

  const isDetailPanelOpen = useMemo(() => {
    return (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction'
  }, [selectedItem, detailMode, selectedItemType])

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      },
      getRoleOptions,
      getInvestorClassificationOptions
    }),
    [getLookupOptions, getRoleOptions, getInvestorClassificationOptions]
  )

  const mergedData = useMemo(() => {
    if (!currentCompany) return {}
    return { ...currentCompany, ...formData }
  }, [currentCompany, formData])

  const companyInfoFields = useMemo(() => {
    if (!currentCompany) return []
    if (mergedData.prog__account_roles && Array.isArray(mergedData.prog__account_roles)) {
      const firstItem = mergedData.prog__account_roles[0]
      if (firstItem && typeof firstItem === 'object' && firstItem.account_roles__account_role_choice_id) {
        mergedData.prog__account_roles = mergedData.prog__account_roles
          .map((role: any) => {
            if (typeof role === 'string') return role
            if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
              return role.account_roles__account_role_choice_id
            }
            return null
          })
          .filter((id: any): id is string => id !== null)
      }
    }
    return getCompanyInfoFields(mergedData, helpers)
  }, [currentCompany, mergedData, helpers])

  const tasksSectionProps = useMemo(
    () => ({
      recordId: accountId,
      recordType: 'company' as const,
      onTaskSelect: handleTaskSelect,
      selectedTaskId:
        selectedItemType === 'task'
          ? selectedItem?.[TaskRecordFields.tasks__id] ||
            (detailMode === 'add-task' || detailMode === 'edit-task' ? 'form-open' : null)
          : null,
      onAddTask: handleAddTask,
      onEditModeChange: handleTaskEditModeChange,
      disableInlineEditing: true,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshTasksRef.current = refreshFn
        if (onRefreshTasksReady) {
          onRefreshTasksReady(refreshFn)
        }
      }
    }),
    [
      accountId,
      selectedItemType,
      selectedItem,
      detailMode,
      handleTaskSelect,
      handleAddTask,
      handleTaskEditModeChange,
      onRefreshTasksReady
    ]
  )

  const interactionsSectionProps = useMemo(
    () => ({
      recordId: accountId,
      recordType: 'company' as const,
      onInteractionSelect: handleInteractionSelect,
      selectedInteractionId:
        selectedItemType === 'interaction'
          ? selectedItem?.[InteractionRecordFields.interactions__id] ||
            (detailMode === 'add-interaction' || detailMode === 'edit-interaction' ? 'form-open' : null)
          : null,
      onAddInteraction: handleAddInteraction,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshInteractionsRef.current = refreshFn
        if (onRefreshInteractionsReady) {
          onRefreshInteractionsReady(refreshFn)
        }
      },
      disableInlineEditing: true
    }),
    [
      accountId,
      selectedItemType,
      selectedItem,
      detailMode,
      handleInteractionSelect,
      handleAddInteraction,
      onRefreshInteractionsReady
    ]
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='drawer-existing-container'
        data-component='DrawerExisting'
        data-company-id={accountId}
        data-selected-item-type={selectedItemType}
        data-detail-mode={detailMode}
        data-hide-interactions={hideInteractions}
        className='h-full'
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <div data-debug='drawer-unified-layout' className='flex flex-col h-full'>
          <div data-debug='drawer-overview-compact' className='flex-shrink-0 mb-2'>
            <FormSection title={SECTION_COMPANY_OVERVIEW} className='mb-2'>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={companyInfoFields}
                onChange={(field, value) => handleFieldChange(field, value)}
                onPatchSuccess={handlePatchSuccess}
                patchId={accountId ?? ''}
                recordId={accountId ?? ''}
                enableAutoPatch={true}
                currentRecord={currentCompany}
              />
            </FormSection>
          </div>

          <Row data-debug='drawer-content-row' gutter={[16, 16]} className='h-full flex-1 min-h-0'>
            <Col
              span={isFullWidthMode ? 10 : isDetailPanelOpen ? 10 : 24}
              className={`h-full flex flex-col min-h-0 ${isFullWidthMode || isDetailPanelOpen ? 'overflow-hidden' : ''}`}
            >
              <div data-debug='drawer-round-overview-section' className='mb-2 flex-shrink-0'>
                <RoundOverviewSection
                  company={currentCompany}
                  formData={formData}
                  handleFieldChange={handleFieldChange}
                  onPatchSuccess={handlePatchSuccess}
                  recordId={accountId ?? null}
                  isReadOnly={true}
                  cardProps={{ className: 'mb-2' }}
                />
              </div>

              <div data-debug='drawer-general-section' className='mb-2 flex-shrink-0'>
                <GeneralSection
                  company={currentCompany}
                  formData={formData}
                  handleFieldChange={handleFieldChange}
                  onPatchSuccess={handlePatchSuccess}
                  recordId={accountId ?? null}
                  cardProps={{ className: 'mb-2' }}
                />
              </div>

              <div data-debug='drawer-investment-summary-section' className='mb-2 flex-shrink-0'>
                <InvestmentSummarySection
                  accountId={accountId ?? null}
                  isDrawer={true}
                  onRefreshRequest={(refreshFn) => {
                    refreshInvestmentMeasuresRef.current = refreshFn
                    if (onRefreshInvestmentMeasuresReady) {
                      onRefreshInvestmentMeasuresReady(refreshFn)
                    }
                  }}
                />
              </div>

              <div data-debug='drawer-tasks-section' className='mb-1 flex-shrink-0'>
                <MemoizedTasksSection {...tasksSectionProps} />
              </div>

              {!hideInteractions && (
                <div data-debug='drawer-interactions-section' className='flex flex-col flex-1 min-h-0'>
                  <div className='h-full min-h-[280px]'>
                    <MemoizedInteractionsSection {...interactionsSectionProps} />
                  </div>
                </div>
              )}
            </Col>

            {(isFullWidthMode || isDetailPanelOpen) && (
              <Col span={14} className='h-full flex flex-col min-h-0'>
                <DetailPanel
                  selectedItem={selectedItem}
                  itemType={selectedItemType}
                  mode={detailMode}
                  onClose={handleDetailClose}
                  onEdit={handleDetailEdit}
                  onSubmit={handleDetailSubmit}
                  onDelete={handleDetailDelete}
                  onDelink={handleDetailDelink}
                  onRefreshInteractions={async () => {
                    if (refreshInteractionsRef.current) {
                      await refreshInteractionsRef.current()
                    }
                  }}
                  onRefreshTasks={handleRefreshTasks}
                />
              </Col>
            )}
          </Row>
        </div>
      </div>
    </DndProvider>
  )
}
